//hooks
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
//api
import { useAppontmentsByUser } from '@api/queries/appointments/appointments';
import { useSingleUser } from '@api/queries/user/user';
//components
import DataCard from '@components/DataCard/DataCard';
import Loading from '@components/Loading/Loading';
import ProfileInfoCard from '@containers/ProfileInfoCard/ProfileInfoCard';
import StatisticCard from '@components/StatisticCard/StatisticCard';
import { Box, Grid, Chip } from '@mui/material';
//icons
import CheckIcon from '@mui/icons-material/Check';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
//interfaces
import { AppointmentStatusEnum } from '@enum/appointmentStatusEnum';
import { AppointmentsByUserResponse } from '@interfaces/appointments/appointments';
import { MRT_ColumnDef } from 'material-react-table';
//helpers
import dayjs from 'dayjs';
import { getColorOfAppointmentStatus } from '@helpers/utility';

export const SingleUserPage = () => {
  const { t } = useTranslation();
  const userId = useParams().userId;

  const [app_card, setCards] = useState({
    canceled: 0,
    completed: 0,
    scheduled: 0,
    in_progress: 0
  });
  const { data: user, isLoading } = useSingleUser(userId ? +userId : null, () => {});
  const { data: appointemnts } = useAppontmentsByUser(
    { userId: userId ? +userId : null },
    () => {},
    (data: AppointmentsByUserResponse[]) => {
      const cards = {
        canceled: 0,
        completed: 0,
        scheduled: 0,
        in_progress: 0
      };
      data.forEach(app => {
        switch (app.status) {
          case AppointmentStatusEnum.canceled: {
            cards.canceled++;
            return;
          }
          case AppointmentStatusEnum.completed: {
            cards.completed++;
            return;
          }
          case AppointmentStatusEnum.scheduled: {
            cards.scheduled++;
            return;
          }
          case AppointmentStatusEnum.in_progress: {
            cards.in_progress++;
            return;
          }
        }
      });

      setCards(cards);
    }
  );

  const appointemntsColmns = useMemo<MRT_ColumnDef<AppointmentsByUserResponse>[]>(
    () => [
      {
        accessorKey: 'id',
        header: t('appointmentId')
      },
      {
        accessorKey: 'sport_center.name',
        header: t('sport_center')
      },

      {
        accessorKey: 'court_sport.sport.name',
        header: t('sport')
      },
      {
        accessorKey: 'datetime_start',
        header: t('date'),
        Cell: ({ row }) => (
          <Box>
            <Box>{dayjs(row.original.datetime_start).format('DD.MM.YYYY.')}</Box>
            <Box>
              {dayjs(row.original.datetime_start).format('HH:mm') +
                ' - ' +
                dayjs(row.original.datetime_end).format('HH:mm')}
            </Box>
          </Box>
        )
      },
      {
        accessorKey: 'status',
        header: t('status'),
        Cell: ({ row }) => (
          <Chip
            label={t(row.original.status).toString()}
            variant="outlined"
            color={getColorOfAppointmentStatus(row.original.status)}
          />
        )
      }
    ],
    []
  );

  if (isLoading) {
    return <Loading />;
  }
  if (!user) {
    return <Box>user does not exist</Box>;
  }

  return (
    <Box p={2}>
      <ProfileInfoCard
        name={user.data.data.first_name + ' ' + user.data.data.last_name}
        image={'data.data.data.profile_image'}
        lastSeen={
          appointemnts && appointemnts.data.data.length > 0
            ? `${t('last_appointment')}: ${dayjs(
                appointemnts.data.data[appointemnts.data.data.length - 1].datetime_start
              ).format('DD.MM.YYYY')}  - ${
                appointemnts.data.data[appointemnts.data.data.length - 1].sport_center.name
              } `
            : ''
        }
        otherInfo={[
          {
            title: t('email'),
            value: user.data.data.email
          },
          {
            title: t('phone'),
            value: user.data.data.phone
          },
          {
            title: t('date_of_birth'),
            value: user.data.data.date_of_birth
          },
          {
            title: t('gender'),
            value: t(user.data.data.gender)
          },
          {
            title: t('created_at'),
            value: user.data.data.created_at ?? ''
          }
        ]}
        isUser={true}
      />

      <Grid container my={2} spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatisticCard
            icon={<CheckIcon />}
            title={app_card.completed.toString()}
            subtitle={t('userProfile.completedTitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatisticCard
            icon={<ScheduleRoundedIcon />}
            title={app_card.scheduled.toString()}
            subtitle={t('userProfile.scheduledTitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatisticCard
            icon={<ClearRoundedIcon />}
            title={app_card.canceled.toString()}
            subtitle={t('userProfile.canceledTitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatisticCard
            icon={<InfoOutlinedIcon />}
            title={app_card.in_progress.toString()}
            subtitle={t('userProfile.in_progressTitle')}
          />
        </Grid>
      </Grid>

      <Box>
        <DataCard
          title={t('userProfile.data_card_title')}
          columns={appointemntsColmns}
          data={appointemnts ? appointemnts.data.data : []}
          showHeader={true}
        />
      </Box>
    </Box>
  );
};
